import update from "immutability-helper";

import {
  CALL_AUTH_FAILED,
  CALL_AUTH_NEEDED,
  CALL_CHECK_SCREEN_SHARE,
  CALL_CLEAR,
  CALL_DIAL_IN_ENABLED,
  CALL_DISCONNECTED,
  CALL_FAILED,
  CALL_FINISH,
  CALL_MUTE_ALL,
  CALL_PEER_JOINED,
  CALL_PEER_LEFT,
  CALL_PEER_SPEAKING,
  CALL_PEER_STREAM_BACK,
  CALL_PEER_STREAM_LOST,
  CALL_PUBLISH_FAILURE,
  CALL_PUBLISH_SUCCESS,
  CALL_RECEIVED_CHAT_HISTORY_MESSAGE,
  CALL_RECEIVED_INFO_MESSAGE,
  CALL_RECEIVED_MESSAGE,
  CALL_RECEIVED_PEER_INFO,
  CALL_REQUEST_PEER_INFO,
  CALL_SCREEN_SHARE_CAP,
  CALL_SCREEN_SHARE_EXT,
  CALL_SCREEN_SHARING,
  CALL_SEND_PEER_INFO,
  CALL_SENT_MESSAGE,
  CALL_SET_PASSWORD,
  CALL_SET_PEER_FRAME_RATE,
  CALL_SET_PEER_RESOLUTION,
  CALL_SETTINGS_PUBLISH_FAILURE,
  CALL_SETTINGS_PUBLISH_SUCCESS,
  CALL_SETTINGS_SET_DEVICES_SUCCESS,
  CALL_SETTINGS_UNPUBLISH_SUCCESS,
  CALL_SETTINGS_UPDATE_AUDIO_VIDEO,
  CALL_SHOW_LOCAL_RECORDING_STATUS,
  CALL_INIT,
  CALL_STARTED,
  CALL_STARTED_RECORDING,
  CALL_STOPPED_RECORDING,
  CALL_TOGGLE_CAMERA,
  CALL_TOGGLE_CHAT,
  CALL_TOGGLE_LOCAL_RECORDING,
  CALL_TOGGLE_MICROPHONE,
  CALL_TOGGLE_PEER_AUDIO,
  CALL_TOGGLE_PEER_VIDEO,
  CALL_UNPUBLISH_SUCCESS,
  CALL_UPDATE_ARCHIVE_STATUS,
  CALL_PARTICIPANT_STARTED_LOCALLY_RECORDING,
} from "../../actions/types";
import { pushGtm, uuidv4 } from "../../../components/utils/util";
import type { PartsInfoEvent, PartUploadProgressEvent } from "../../../lib/localRecordingNew";

const CallStatusValues = [
  "STATUS_CONNECTING",
  "STATUS_RECONNECTING",
  "STATUS_STARTED",
  "STATUS_FINISHED",
  "STATUS_DISCONNECTED",
  "STATUS_FAILED",
  "STATUS_AUTH_NEEDED",
  "STATUS_AUTH_FAILED",
] as const;

export const [
  STATUS_CONNECTING,
  STATUS_RECONNECTING,
  STATUS_STARTED,
  STATUS_FINISHED,
  STATUS_DISCONNECTED,
  STATUS_FAILED,
  STATUS_AUTH_NEEDED,
  STATUS_AUTH_FAILED,
] = CallStatusValues;

export type CallStatus = typeof CallStatusValues[number];

const connectedStatuses: CallStatus[] = [STATUS_STARTED, STATUS_CONNECTING, STATUS_RECONNECTING];

export const isOnline = (status: CallStatus) => connectedStatuses.includes(status);

export const STREAM_STATUS_NORMAL = "STREAM_STATUS_NORMAL";
export const STREAM_STATUS_AUDIO_ONLY = "STREAM_STATUS_AUDIO_ONLY";

export type StreamId = string;
export type VisitorId = string;

// Leaving this code in to make testing UI with many participants on the call. Just flip the
// flag, do your thing and flip it back
const addFakeParticipants = false;
const fillerNames = (
  "Alice Bob Carol Carlos Charlie Chuck Craig Dan Dave David Erin Eve Fain Faythe Frank Grace Heidi " +
  "Ivan Judy Mallory Mike Niaj Olivia Oscar Padan Pat Peggy Rupert Sybil Trent Ted Trudy Victor Vanna Walter Wendy"
).split(" ");

export interface Peer {
  source: unknown;
  audioEnabled: boolean;
  videoEnabled: boolean;
  streamStatus: string;
  isScreen: boolean;
  name: string;
  source_type: string;
  user_id: string;
  visitor_id: string;
}

export interface LocalRecordingInfo extends Omit<PartUploadProgressEvent, "partId">, PartsInfoEvent {
  localRecordingUploadSucceeded: boolean;
  localRecordingUploadFailed: boolean;
  localRecordingStopped: boolean;
  localRecordingStarted: boolean;
  sessionId: string;
}

export const initialLocalRecordingInfo: LocalRecordingInfo = {
  localRecordingUploadSucceeded: false,
  localRecordingUploadFailed: false,
  localRecordingStopped: false,
  localRecordingStarted: false,
  uploadedBytes: 0,
  partsUploaded: 0,
  partBytes: 0,
  parts: [],
  sessionId: "",
};

export interface ParticipantInfo {
  name?: string;
  // for the time being "cohost" doesn't do much but its a place holder
  // for where we are envisioning to take the participants in the future
  role: "host" | "cohost" | "guest";
  localRecordings: {
    [key: string]: LocalRecordingInfo;
  };
  localRecordingCapable?: boolean;
  isVideoRecorded?: boolean;
  isPresent: boolean;
}

const initialParticipantInfo: ParticipantInfo = {
  name: null,
  role: "guest",
  localRecordings: {},
  localRecordingCapable: null,
  isVideoRecorded: null,
  isPresent: true,
};

export type ParticipantMap = Map<VisitorId, ParticipantInfo>;
export type ParticipantMapEntry = [VisitorId, ParticipantInfo];

export interface CallState {
  status: CallStatus;
  error: Error; // Can (should) this type be in Redux??
  sessionPassword: string;
  publisher: any;
  peers: Map<StreamId, Peer>;
  participants: ParticipantMap;
  messages: Map<any, any>;
  recording: boolean;
  localRecording: boolean;
  localRecordingId: string;
  localRecordingsAllowed: boolean;
  localRecordingsPolyfill: boolean;
  publishing: {
    audio: boolean;
    video: boolean;
  };
  callError: any;
  settingsError: any;
  settingsPublisher: any;
  audioDevices: string[];
  videoDevices: string[];
  outputDevices: string[];
  audioSource: string;
  videoSource: string;
  audioOutput: string;
  storyId: string;
  creator: any;
  author: any;
  groupId: string;
  token: string;
  recorded: boolean;
  initialDuration: number;
  lastSpeaker: any;
  sessionId: string;
  screenShareSupported: boolean;
  screenSharing: boolean;
  showScreenShareExt: boolean;
  newMessage: any;
  newSentMessage: any;
  messageNumber: number;
  typingSender: string;
  sendHistoryChat: Map<any, any>;
  peerToUpdate: any;
  phone_number: any;
  dial_in_code: any;
  questions: any[];
  prepsession: any;
  showChat: boolean;
  featureFlags: any;
  showLocalRecordingStatus: boolean;
}

const initialState: CallState = {
  status: null,
  error: null,
  sessionPassword: "",
  publisher: null,

  // Identifies each remote stream which is part of the call session. A screen share
  // is considered a separate stream and therefore will be represented by a separate
  // entry in this map. "me" DOES NOT exist here.  The origin of this field goes back
  // to initial tokbox call implementation.
  peers: new Map<StreamId, Peer>(),

  // Identifies each unique participant in a call session. A participant may connect
  // or disconnect and may permanently leave the call, but once they joined, there
  // will be a record of them in this collection
  participants: new Map<VisitorId, ParticipantInfo>(),

  messages: new Map(),
  recording: false,
  localRecordingId: null,
  localRecording: false,
  localRecordingsAllowed: false,
  localRecordingsPolyfill: false,
  publishing: {
    audio: true,
    video: true,
  },
  callError: null,
  settingsError: null,
  settingsPublisher: null,
  audioDevices: [],
  videoDevices: [],
  outputDevices: [],
  audioSource: null,
  videoSource: null,
  audioOutput: null,
  storyId: null,
  creator: null,
  author: null,
  groupId: null,
  token: null,
  recorded: true,
  initialDuration: 0,
  lastSpeaker: null,
  sessionId: null,
  screenShareSupported: false,
  screenSharing: false,
  showScreenShareExt: false,
  newMessage: null,
  newSentMessage: null,
  messageNumber: 0,
  typingSender: "",
  sendHistoryChat: new Map(),
  peerToUpdate: null,
  phone_number: null,
  dial_in_code: null,
  questions: [],
  prepsession: null,
  showChat: false,
  featureFlags: null,
  showLocalRecordingStatus: false,
};

interface CallAction {
  type: string;
  payload: any;
  [key: string]: any;
}

export const updateParticipants = (updatableParticipants: Map<VisitorId, ParticipantInfo>, payload) => {
  const participantUpdates = (Array.isArray(payload) ? payload : [payload]).map(
    (value: { visitorId: string; overwrite?: boolean; data: any }): [string, ParticipantInfo] => {
      const { visitorId, overwrite, data } = value;
      if (overwrite) {
        return [visitorId, data as ParticipantInfo];
      } else {
        let participant = updatableParticipants.get(visitorId) || initialParticipantInfo;
        if (typeof data.localRecordings === "object") {
          Object.keys(data.localRecordings).forEach((key) => {
            if (!(key in participant.localRecordings)) {
              participant = update(participant, { localRecordings: { [key]: { $set: initialLocalRecordingInfo } } });
            }
          });
        }
        return [visitorId, update(participant, data)];
      }
    }
  );

  return update(updatableParticipants, { $add: participantUpdates });
};

export default function reducer(state = initialState, action: CallAction): CallState {
  if (action.type.startsWith("call/")) {
    // console.log(`%cAction: %c${action.type}`, "font-weight: bold", "color: orange");
    // send GTM event data (with some exceptions)
    if (
      ![
        CALL_SCREEN_SHARE_CAP,
        CALL_CHECK_SCREEN_SHARE,
        CALL_SEND_PEER_INFO,
        CALL_REQUEST_PEER_INFO,
        CALL_RECEIVED_PEER_INFO,
        CALL_SET_PEER_RESOLUTION,
        CALL_SET_PEER_FRAME_RATE,
        CALL_PEER_SPEAKING,
      ].includes(action.type)
    ) {
      pushGtm({
        event: "call_action",
        call_action: String(action.type).replace("call/", ""),
        story_id: state.storyId,
      });
    }
  }

  switch (action.type) {
    case CALL_CLEAR:
      return { ...initialState };
    case CALL_AUTH_NEEDED:
      return update(state, {
        status: { $set: STATUS_AUTH_NEEDED },
        author: { $set: action.payload.author },
      });
    case CALL_AUTH_FAILED:
      return update(state, {
        status: { $set: STATUS_AUTH_FAILED },
      });
    case CALL_SET_PASSWORD: {
      const updateMap: any = {
        sessionPassword: { $set: action.sessionPassword },
      };
      if (state.status === STATUS_AUTH_FAILED) {
        updateMap.status = { $set: STATUS_AUTH_NEEDED };
      }
      return update(state, updateMap);
    }
    case CALL_DIAL_IN_ENABLED:
      return update(state, {
        phone_number: { $set: action.data.phone_number },
        dial_in_code: { $set: action.data.dial_in_code },
      });
    case CALL_INIT:
      return update(state, {
        status: { $set: STATUS_CONNECTING },
      });
    case CALL_STARTED:
      return update(state, {
        status: { $set: STATUS_STARTED },
        storyId: { $set: action.data.storyId },
        groupId: { $set: action.data.groupId },
        creator: { $set: action.data.creator },
        author: { $set: action.data.author },
        sessionId: { $set: action.data.sessionId },
        token: { $set: action.data.token },
        questions: { $set: action.data.questions },
        localRecording: { $set: action.data.localRecording },
        localRecordingsAllowed: { $set: action.data.localRecordingsAllowed },
        localRecordingsPolyfill: { $set: action.data.localRecordingsPolyfill },
        phone_number: { $set: action.data.phone_number },
        dial_in_code: { $set: action.data.dial_in_code },
        // Backend will only return session password, if the caller is the author
        // of the session. For anyone else, let's make sure we preserve whatever
        // password user may have already entered into the login modal dialog
        sessionPassword: {
          $set: action.data.author ? action.data.sessionPassword : state.sessionPassword,
        },
        prepsession: { $set: action.data.prepsession },
        initialDuration: { $set: action.data.initialDuration },
        featureFlags: { $set: action.data.featureFlags },
        participants: {
          $add: (addFakeParticipants ? fillerNames : []).map((name): [string, ParticipantInfo] => [
            uuidv4(),
            { name, role: "guest", localRecordings: {}, isPresent: true },
          ]),
        },
      });
    case CALL_DISCONNECTED:
      return update(state, {
        status: { $set: STATUS_RECONNECTING },
      });
    case CALL_FINISH:
      return update(initialState, {
        status: { $set: STATUS_FINISHED },
        peers: { $set: new Map() },
        recording: { $set: false },
        lastSpeaker: { $set: null },
        publisher: { $set: null },
      });
    case CALL_FAILED:
      return update(state, {
        status: { $set: STATUS_FAILED },
        error: { $set: action.error },
      });

    case CALL_PEER_JOINED: {
      const peer = {
        source: action.source,
        audioEnabled: action.audioEnabled,
        videoEnabled: action.videoEnabled,
        streamStatus: STREAM_STATUS_NORMAL,
        isScreen: !!action.isScreen,
        name: action.name,
        source_type: action.source_type,
        user_id: action.user_id,
        visitor_id: action.visitor_id,
      };

      return update(state, {
        peers: { $add: [[action.id, peer]] },
        ...(peer.isScreen
          ? {}
          : {
              participants: {
                $set: updateParticipants(state.participants, {
                  visitorId: peer.visitor_id,
                  data: { isPresent: { $set: true } },
                }),
              },
            }),
        // if new peer and messages send chat history
        ...(state.messages.size > 0
          ? {
              sendHistoryChat: { $set: state.messages },
              peerToUpdate: { $set: action.id },
            }
          : {}),
      });
    }

    case CALL_PEER_LEFT: {
      const peer = state.peers.get(action.id);
      let newState = state;
      if (peer) {
        newState = update(newState, {
          peers: { $remove: [action.id] },
        });
        if (!peer.isScreen && Array.from(newState.peers.values()).every((p) => p.visitor_id !== peer.visitor_id)) {
          newState = update(newState, {
            participants: {
              $set: updateParticipants(newState.participants, {
                visitorId: peer.visitor_id,
                data: { isPresent: { $set: false } },
              }),
            },
          });
        }
      }
      return newState;
    }

    case CALL_PUBLISH_FAILURE:
      return update(state, {
        error: { $set: action.error },
        recording: { $set: false },
      });

    case CALL_UNPUBLISH_SUCCESS:
    case CALL_PUBLISH_SUCCESS:
      return update(state, {
        publisher: { $set: action.publisher },
      });

    case CALL_TOGGLE_CAMERA:
      return update(state, {
        publishing: { video: { $set: !state.publishing.video } },
      });

    case CALL_TOGGLE_MICROPHONE:
      return update(state, {
        publishing: { audio: { $set: !state.publishing.audio } },
      });

    case CALL_TOGGLE_CHAT:
      return update(state, { showChat: { $set: !state.showChat } });

    case CALL_PARTICIPANT_STARTED_LOCALLY_RECORDING:
      return update(state, {
        localRecordingId: { $set: action.data.recordingId },
      });
    case CALL_STARTED_RECORDING:
      return update(state, {
        recording: { $set: true },
        recorded: { $set: true },
      });

    case CALL_STOPPED_RECORDING:
      // Due to some magic in call middleware (see detailed comments there), expect
      // that this event will be called more than once
      return update(state, { recording: { $set: false } });

    case CALL_UPDATE_ARCHIVE_STATUS:
      return update(state, { recorded: { $set: !!action.status } });

    case CALL_TOGGLE_LOCAL_RECORDING:
      return update(state, { localRecording: { $set: !state.localRecording } });

    case CALL_TOGGLE_PEER_AUDIO: {
      let peer = state.peers.get(action.peerID);
      if (!peer) return state;
      peer = update(peer, { audioEnabled: { $set: !peer.audioEnabled } });
      return update(state, {
        peers: { $add: [[action.peerID, peer]] },
      });
    }

    case CALL_TOGGLE_PEER_VIDEO: {
      let peer = state.peers.get(action.peerID);
      if (!peer) return state;
      peer = update(peer, { videoEnabled: { $set: action.enabled } });
      return update(state, {
        peers: { $add: [[action.peerID, peer]] },
      });
    }

    case CALL_PEER_STREAM_LOST: {
      let peer = state.peers.get(action.peerID);
      if (!peer) return state;
      peer = update(peer, { streamStatus: { $set: STREAM_STATUS_AUDIO_ONLY } });
      return update(state, {
        peers: { $add: [[action.peerID, peer]] },
      });
    }

    case CALL_MUTE_ALL: {
      const newPeers = new Map();
      state.peers.forEach((peer, id) => {
        newPeers.set(id, update(peer, { audioEnabled: { $set: false } }));
      });
      return update(state, { peers: { $set: newPeers } });
    }

    case CALL_PEER_SPEAKING: {
      return update(state, { lastSpeaker: { $set: action.peerID } });
    }

    case CALL_PEER_STREAM_BACK: {
      let peer = state.peers.get(action.peerID);
      if (!peer) return state;
      peer = update(peer, { streamStatus: { $set: STREAM_STATUS_NORMAL } });
      return update(state, {
        peers: { $add: [[action.peerID, peer]] },
      });
    }

    case CALL_SCREEN_SHARE_CAP: {
      return update(state, {
        screenShareSupported: { $set: action.supported },
      });
    }

    case CALL_SCREEN_SHARE_EXT: {
      return update(state, { showScreenShareExt: { $set: action.visible } });
    }

    case CALL_SCREEN_SHARING: {
      return update(state, { screenSharing: { $set: action.sharing } });
    }

    case CALL_SENT_MESSAGE: {
      return update(state, { newSentMessage: { $set: action.newSentMessage } });
    }

    case CALL_RECEIVED_MESSAGE: {
      const now = new Date();
      state.messageNumber += 1;
      const newMessageObject = {
        sender: action.sender,
        peer: action.peerID,
        time: now.getTime(),
        text: action.newMessage.trim(),
        isMine: action.isMine,
      };
      return update(state, {
        messages: { $add: [[state.messageNumber, newMessageObject]] },
      });
    }

    case CALL_RECEIVED_INFO_MESSAGE:
      if (!action.isMine) {
        if (!action.typing) {
          return update(state, { typingSender: { $set: "" } });
        } else {
          return update(state, { typingSender: { $set: action.sender } });
        }
      } else {
        return state;
      }

    case CALL_RECEIVED_CHAT_HISTORY_MESSAGE:
      if (!action.isMine) {
        const chatHistory = action.chatHistory;
        if (
          (state.peerToUpdate && state.peerToUpdate !== action.receivingPeer) ||
          chatHistory.size < state.messageNumber - 1
        ) {
          return state;
        }
        chatHistory.forEach((value, key) => {
          value.isMine = false;
          chatHistory.set(key, value);
        });
        state.peerToUpdate = "";
        return update(state, {
          messages: { $set: action.chatHistory },
          messageNumber: { $set: <number>chatHistory.size + 2 },
        });
      } else {
        return update(state, { sendHistoryChat: { $set: new Map() } });
      }

    case CALL_RECEIVED_PEER_INFO:
      return update(state, {
        participants: { $set: updateParticipants(state.participants, action.payload) },
      });

    case CALL_SETTINGS_UNPUBLISH_SUCCESS:
    case CALL_SETTINGS_PUBLISH_SUCCESS:
      return update(state, {
        settingsPublisher: { $set: action.settingsPublisher },
        settingsError: { $set: null },
      });

    case CALL_SETTINGS_PUBLISH_FAILURE:
      return update(state, {
        settingsError: { $set: action.error },
      });

    case CALL_SETTINGS_SET_DEVICES_SUCCESS:
      return update(state, {
        audioDevices: { $set: action.audioDevices },
        videoDevices: { $set: action.videoDevices },
        outputDevices: { $set: action.outputDevices },
      });

    case CALL_SETTINGS_UPDATE_AUDIO_VIDEO: {
      const { audioSource, videoSource, audioOutput } = action.payload;
      return update(state, {
        ...(audioSource ? { audioSource: { $set: audioSource } } : {}),
        ...(videoSource ? { videoSource: { $set: videoSource } } : {}),
        ...(audioOutput ? { audioOutput: { $set: audioOutput } } : {}),
      });
    }

    case CALL_SHOW_LOCAL_RECORDING_STATUS:
      return update(state, {
        showLocalRecordingStatus: { $set: action.payload ?? !state.showLocalRecordingStatus },
      });

    default:
      return state;
  }
}
